import { fetchAuthSession, signOut as cognitoSignOut } from "aws-amplify/auth";
import axios from "axios";

import constants from "../../constants";
import { clearAdIntent } from "../../hooks/AdIntent";
import { forgetEditingSite } from "../../hooks/EditingSite";
import { getTrafficOrigin, markTrafficOriginSent, TrafficOrigin } from "../trafficOrigin";

const LOCAL_STORAGE_AUTH_KEY = "Keepo.accessToken";
const AUTH_API_BASE = constants.api;
const AUTH_LOGIN_PATH = import.meta.env.VITE_APP_AUTH_LOGIN_PATH || "/auth/login";
const AUTH_REGISTER_PATH = import.meta.env.VITE_APP_AUTH_REGISTER_PATH || "/auth/register";
const AUTH_VERIFY_EMAIL_PATH = import.meta.env.VITE_APP_AUTH_VERIFY_EMAIL_PATH || "/auth/verify-email";
const AUTH_RESEND_VERIFICATION_CODE_PATH =
  import.meta.env.VITE_APP_AUTH_RESEND_VERIFICATION_CODE_PATH || "/auth/resend-verification-code";
const AUTH_CHECK_EMAIL_PATH = import.meta.env.VITE_APP_AUTH_CHECK_EMAIL_PATH || "/auth/check-email";
const AUTH_RECOVERY_PASSWORD_PATH = import.meta.env.VITE_APP_AUTH_RECOVERY_PASSWORD_PATH || "/auth/recovery-password";
const AUTH_RESET_PASSWORD_PATH = import.meta.env.VITE_APP_AUTH_RESET_PASSWORD_PATH || "/auth/reset-password";

const buildUrl = (path: string) => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${AUTH_API_BASE}${normalizedPath}`;
};

const extractErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data as
      | {
          message?: unknown;
          error?: unknown;
          code?: unknown;
          errorCode?: unknown;
        }
      | undefined;

    const fromPayload = responseData?.message || responseData?.error || responseData?.code || responseData?.errorCode;

    if (typeof fromPayload === "string" && fromPayload.trim()) {
      return fromPayload;
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "AUTH_GENERIC_ERROR";
};

const throwMappedApiError = (error: unknown): never => {
  throw new Error(extractErrorMessage(error));
};

const extractAccessToken = (responseData: any): string | undefined => {
  return responseData?.data?.token;
};

export type RegisterPayload = {
  email: string;
  name: string;
  password: string;
  trafficOrigin?: TrafficOrigin;
};

export type RecoveryPasswordStep1Response = {
  email: string;
  recoveryCodeSent: boolean;
};

export type RecoveryPasswordStep2Response = {
  email: string;
  recoveryAllowed: boolean;
  recoveryToken: string;
};

export type ResetPasswordPayload = {
  email: string;
  recoveryToken: string;
  password: string;
};

export type ResetPasswordResponse = {
  email: string;
  passwordChanged: boolean;
};

export type CheckEmailNextStep = "register" | "login" | "verify_email";

const CHECK_EMAIL_NEXT_STEPS: CheckEmailNextStep[] = ["register", "login", "verify_email"];

const isCheckEmailNextStep = (value: unknown): value is CheckEmailNextStep => {
  return typeof value === "string" && CHECK_EMAIL_NEXT_STEPS.includes(value as CheckEmailNextStep);
};

export const signInWithEmailAndPassword = async (email: string, password: string) => {
  try {
    const response = await axios.post(buildUrl(AUTH_LOGIN_PATH), {
      email,
      password,
    });

    const accessToken = extractAccessToken(response.data);
    if (!accessToken) {
      throw new Error("AUTH_TOKEN_NOT_FOUND");
    }

    storeLocalAuthToken(accessToken);
    return response.data;
  } catch (error) {
    throwMappedApiError(error);
  }
};

export const signUpWithEmailAndPassword = async (payload: RegisterPayload) => {
  try {
    const trafficOrigin = getTrafficOrigin();
    const response = await axios.post(buildUrl(AUTH_REGISTER_PATH), {
      ...payload,
      ...(trafficOrigin ? { trafficOrigin } : {}),
    });
    if (trafficOrigin) {
      markTrafficOriginSent(trafficOrigin);
    }
    return response.data;
  } catch (error) {
    throwMappedApiError(error);
  }
};

export const checkEmail = async (email: string): Promise<CheckEmailNextStep> => {
  try {
    const response = await axios.post(buildUrl(AUTH_CHECK_EMAIL_PATH), {
      email,
    });

    const nextStepRaw = response.data?.data?.nextStep || response.data?.nextStep;
    const nextStep = typeof nextStepRaw === "string" ? nextStepRaw.toLowerCase() : nextStepRaw;
    if (isCheckEmailNextStep(nextStep)) {
      return nextStep;
    }

    const exists = response.data?.exists;
    const available = response.data?.available;
    const canRegister = response.data?.canRegister;

    if (exists === true || available === false || canRegister === false) {
      return "login";
    }

    if (exists === false || available === true || canRegister === true) {
      return "register";
    }

    throw new Error("AUTH_CHECK_EMAIL_INVALID_RESPONSE");
  } catch (error) {
    throwMappedApiError(error);
  }
};

export const confirmSignUpCode = async (email: string, verificationCode: string) => {
  try {
    const response = await axios.post(buildUrl(AUTH_VERIFY_EMAIL_PATH), {
      email,
      verificationCode,
    });
    return response.data;
  } catch (error) {
    throwMappedApiError(error);
  }
};

export const resendSignUpCode = async (email: string) => {
  try {
    const response = await axios.post(buildUrl(AUTH_RESEND_VERIFICATION_CODE_PATH), {
      email,
    });
    return response.data;
  } catch (error) {
    throwMappedApiError(error);
  }
};

export const requestRecoveryPassword = async (email: string): Promise<RecoveryPasswordStep1Response> => {
  try {
    const response = await axios.post(buildUrl(AUTH_RECOVERY_PASSWORD_PATH), {
      email,
    });

    return response.data?.data || response.data;
  } catch (error) {
    throwMappedApiError(error);
  }
};

export const verifyRecoveryPasswordCode = async (
  email: string,
  verificationCode: string
): Promise<RecoveryPasswordStep2Response> => {
  try {
    const response = await axios.post(buildUrl(AUTH_RECOVERY_PASSWORD_PATH), {
      email,
      verificationCode,
    });

    return response.data?.data || response.data;
  } catch (error) {
    throwMappedApiError(error);
  }
};

export const resetPassword = async (payload: ResetPasswordPayload): Promise<ResetPasswordResponse> => {
  try {
    const response = await axios.post(buildUrl(AUTH_RESET_PASSWORD_PATH), payload);
    return response.data?.data || response.data;
  } catch (error) {
    throwMappedApiError(error);
  }
};

export const fetchLocalAuthToken = () => {
  return localStorage.getItem(LOCAL_STORAGE_AUTH_KEY) || null;
};

export const storeLocalAuthToken = (token: string) => {
  localStorage.setItem(LOCAL_STORAGE_AUTH_KEY, token);
};

export const clearLocalAuthToken = () => {
  localStorage.removeItem(LOCAL_STORAGE_AUTH_KEY);
};

export const signOut = async () => {
  // another account may sign in on this tab: it doesn't reopen this one's Keepo
  forgetEditingSite();
  // nor does it carry this one's ad page on to the next sign up
  clearAdIntent();

  if (fetchLocalAuthToken()) {
    clearLocalAuthToken();
    window.location.href = `${window.location.origin}/`;
    return;
  }

  await cognitoSignOut();
};

export const isAuthenticated = async () => {
  if (fetchLocalAuthToken()) {
    return true;
  }

  try {
    const { tokens } = await fetchAuthSession();
    return !!tokens?.accessToken;
  } catch (e) {
    return false;
  }
};
